/**
 * 파트너 상세의 확인 창 — [취소] [실행]. 실행이 실패하면 창을 남겨 둔다(사유는 토스트).
 *
 * @spec:2026-10-14-phai-admin-외부연동키-관리화면 — D-9
 *
 * Detail.tsx에 있던 것을 그대로 꺼냈다. K-MRS 접근코드 절(`kmrs/`)이 폐기 확인에 같은 창을
 * 쓰는데, Detail.tsx가 그 절을 import하므로 거꾸로 import하면 순환이 된다. 그래서 공용 모듈로 둔다.
 */
import React, { useState } from "react";
import Button from "components/atoms/Button";
import { ConfirmManager } from "components/organisms/CommonUI";
import { REL_NOTICE } from "utils/partnerAdmin";
import { Notice } from "./style";

/** 확인 창 본문 — 운영(REL)이면 「운영 환경입니다」를 한 줄 더 넣는다(D-9). */
export const ConfirmText: React.FC<{ lines: string[]; isRel: boolean }> = ({ lines, isRel }) => (
  <div data-testid="partner-confirm-text">
    {isRel && (
      <Notice $tone="warn" style={{ marginBottom: 8 }}>
        {REL_NOTICE}
      </Notice>
    )}
    {lines.map((line) => (
      <div key={line}>{line}</div>
    ))}
  </div>
);

/** 확인 창의 실행 버튼 — 요청 중에는 막아 두 번 눌러도 한 번만 보낸다. */
export const ConfirmActionButton: React.FC<{
  label: string;
  negative?: boolean;
  onConfirm: () => Promise<unknown>;
}> = ({ label, negative, onConfirm }) => {
  const [pending, setPending] = useState(false);
  return (
    <Button
      type="Text"
      size="Medium"
      variant="Contained"
      color={negative ? "Negative" : "Primary"}
      disabled={pending}
      onClick={async () => {
        setPending(true);
        try {
          await onConfirm();
          ConfirmManager.hide();
        } catch {
          // 사유는 훅이 토스트로 보여준다. 창은 남겨 둔다.
        } finally {
          setPending(false);
        }
      }}
      data-testid="partner-confirm-ok"
    >
      {label}
    </Button>
  );
};

/** 확인 창 — [취소] [실행]. 실행이 실패하면 창을 남겨 둔다(사유는 토스트). */
export const showConfirm = (opts: {
  title: string;
  lines: string[];
  isRel: boolean;
  confirmLabel: string;
  negative?: boolean;
  onConfirm: () => Promise<unknown>;
}) => {
  ConfirmManager.show({
    title: opts.title,
    body: <ConfirmText lines={opts.lines} isRel={opts.isRel} />,
    buttons: [
      <Button key="cancel" type="Text" size="Medium" variant="Ghost" color="Secondary" onClick={() => ConfirmManager.hide()}>
        취소
      </Button>,
      <ConfirmActionButton key="ok" label={opts.confirmLabel} negative={opts.negative} onConfirm={opts.onConfirm} />,
    ],
    showBackdrop: true,
    backdropClose: true,
    showCloseButton: false,
  });
};
